import React from 'react';
import { useIntl } from 'react-intl';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { matchPath, useLocation } from 'react-router';
import { NavLink } from 'react-router-dom';

import { $tid } from '@provider-portal/common/utils/testIdConfig';
import { List } from '@provider-portal/design-common/kalep/components//List/List';
import { ListItem } from '@provider-portal/design-common/kalep/components//ListItem/ListItem';
import { ListItemButton } from '@provider-portal/design-common/kalep/components/ListItemButton/ListItemButton';
import { ListItemText } from '@provider-portal/design-common/kalep/components/ListItemText/ListItemText';

import { ConfigMenuItem } from 'root/navigation/navTypes';
import { MenuContext } from 'root/navigation/navStructureUtils';

interface SideMenuItemProps {
  text: string;
  index: string;
  isSelected: boolean;
  disabled: boolean;
  href?: string;
  onMenuItemClick?: (menuItem: NavBarMenuItem) => void;
}

const navigationItemIdsToTestIdsMapping = {
  dashboard: $tid.components.SideBar.Analytics.DashBoard,
  merchantWeeklyReport: $tid.components.SideBar.Analytics.WeeklyReport,
  itemReport: $tid.components.SideBar.Analytics.DetailedReports,
  invoicesAndSalesReports: $tid.components.SideBar.Analytics.InvoicesAndSales,
  orderHistory: $tid.components.SideBar.Analytics.OrderHistory,
  'org-summary': $tid.components.SideBar.MenuEditor.OrgSummary,
  'my-menu': $tid.components.SideBar.MenuEditor.MyMenu,
  items: $tid.components.SideBar.MenuEditor.Items,
  modifiers: $tid.components.SideBar.MenuEditor.Modifiers,
  timetables: $tid.components.SideBar.MenuEditor.Timetables,
};

const getTestIdForItemId = (itemId: string): string => {
  if (navigationItemIdsToTestIdsMapping[itemId]) {
    return navigationItemIdsToTestIdsMapping[itemId];
  }

  return '';
};

export const NavMenuItem = ({
  text,
  index,
  isSelected,
  disabled,
  href,
  onMenuItemClick,
}: SideMenuItemProps) => {
  const handleClick = React.useCallback(() => {
    onMenuItemClick?.({
      id: index,
      titleIntlId: text,
      href,
      disabled,
    });
  }, [disabled, href, index, onMenuItemClick, text]);

  return (
    <ListItem key={`${text}-${index}`} disablePadding sx={{ mb: 1 }}>
      <ListItemButton
        selected={isSelected}
        aria-label={text}
        disabled={disabled}
        component={NavLink}
        {...{ to: href }}
        disableRipple
        data-testid={getTestIdForItemId(index)}
        onClick={handleClick}
      >
        <ListItemText primary={text} />
      </ListItemButton>
    </ListItem>
  );
};
export interface NavBarMenuItem extends ConfigMenuItem {
  disabled?: boolean;
}
export interface NavBarMenuProps {
  menuItems?: NavBarMenuItem[];
  onMenuItemClick?: (menuItem: NavBarMenuItem) => void;
}

export function NavBarMenu({ menuItems, onMenuItemClick }: NavBarMenuProps) {
  const location = useLocation();
  const intl = useIntl();
  const menuContext = React.useContext(MenuContext);

  const menuItemsLayout = React.useMemo(() => {
    return menuItems.map((item) => {
      if (item.hidden) {
        return null;
      }
      const isPathMatch = matchPath(location.pathname, {
        path: item.href,
      });

      const shouldOverrideActiveState = menuContext?.activeMenuEntryIdOverride === item.id;
      const isSelected = isPathMatch || shouldOverrideActiveState;
      return (
        <NavMenuItem
          key={item.id}
          text={intl.formatMessage({ id: item.titleIntlId })}
          index={item.id}
          isSelected={!!isSelected}
          disabled={item.disabled || false}
          href={item.href}
          onMenuItemClick={onMenuItemClick}
        />
      );
    });
  }, [menuItems, location.pathname, menuContext?.activeMenuEntryIdOverride, intl, onMenuItemClick]);

  return (
    <PerfectScrollbar options={{ suppressScrollX: true }}>
      <List>{menuItemsLayout}</List>
    </PerfectScrollbar>
  );
}
